import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminAPI, authAPI, contentAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Loader } from '@/components/ui/loader';

export const AdminServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    category: 'corporate_recovery',
    title: { en: '', hi: '' },
    description: { en: '', hi: '' },
    icon: '',
    visible: true,
    order: 0
  });

  const checkAuth = useCallback(async () => {
    try {
      await authAPI.getMe();
      fetchServices();
    } catch (error) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const fetchServices = async () => {
    try {
      const res = await contentAPI.getServices();
      setServices(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminAPI.updateService(editing, formData);
        toast.success('Service updated');
      } else {
        await adminAPI.createService(formData);
        toast.success('Service created');
      }
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error('Error saving service');
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      category: 'corporate_recovery',
      title: { en: '', hi: '' },
      description: { en: '', hi: '' },
      icon: '',
      visible: true,
      order: 0
    });
    setEditing(null);
  };

  const handleEdit = (service) => {
    setFormData(service);
    setEditing(service.slug);
  };

  const handleDelete = async (slug) => {
    if (window.confirm('Delete this service?')) {
      try {
        await adminAPI.deleteService(slug);
        toast.success('Service deleted');
        fetchServices();
      } catch (error) {
        toast.error('Error deleting');
      }
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg.gray-100"><Loader className="text-secondary" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 admin-panel">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="ghost" size="sm"><ArrowLeft size={18} className="mr-2" />Back</Button></Link>
            <h1 className="text-2xl font-bold">Manage Services</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">{editing ? 'Edit' : 'Add'} Service</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Slug</label>
                <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} disabled={editing} required placeholder="e.g., corporate-debt-recovery" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="corporate_recovery">Corporate Recovery</SelectItem>
                    <SelectItem value="npa_resolution">NPA Resolution</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Hindi)</label>
                <Input value={formData.title.hi} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, hi: e.target.value } })} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description (English)</label>
                <Textarea value={formData.description.en} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })} required rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description (Hindi)</label>
                <Textarea value={formData.description.hi} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, hi: e.target.value } })} required rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Order</label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} required />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">{editing ? 'Update' : 'Create'}</Button>
                {editing && <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>}
              </div>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-6">Services List</h2>
            <div className="space-y-4">
              {services.map((service) => (
                <div key={service.slug} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded mb-2">{service.category}</span>
                      <h3 className="font-semibold">{service.title.en}</h3>
                      <p className="text-xs text-gray-500 mt-1">{service.description.en.substring(0, 60)}...</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(service)}><Edit size={16} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(service.slug)} className="text-red-600"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};